/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import BaseRouter from '../Router';
import globalHistory from '../history';
import PropTypes from 'prop-types';
import React from 'react';
import RouterContext from './Context';

class Router extends React.Component {
  constructor(props, context) {
    super(props);
    this.state = {route: '/'};
    this.history = props.history || globalHistory;
    if (!context) {
      let {hash, pathname, search} = this.history.location;
      this.state.route = pathname + search + hash;
    }
  }

  componentDidMount() {
    // If this is a root-level router, setup a history handler
    if (!this.context) {
      this.unlisten = this.history.listen(location => {
        let {hash, pathname, search} = location;
        this.setState({route: pathname + search + hash});
      });
    }
  }

  componentWillUnmount() {
    if (this.unlisten) {
      this.unlisten();
    }
  }

  render() {
    let router = new BaseRouter();

    // Work around enzyme where context is consistently an empty object
    let hasContext = typeof this.context === 'object' && Object.keys(this.context).length > 0;

    // Provide context to children
    let context = {
      fullPrefix: hasContext ? this.context.fullPrefix : '',
      prefix: hasContext ? this.context.prefix : '',
      route: hasContext ? this.context.route : this.state.route,
      router
    };

    // Create a router for the child Route components
    React.Children.forEach(this.props.children, child => {
      if (child && React.isValidElement(child)) {
        if (child.props.redirect) {
          let redirect =
            typeof child.props.redirect === 'function' ? child.props.redirect(context) : child.props.redirect;
          router.redirect(child.props.path || '', redirect);
          return;
        }
        let path = child.props.path || '';
        let exact = child.props.exact || path.endsWith('/');
        router.add(path + (exact ? '' : '/(.*)?'), ctx => React.cloneElement(child, {ctx}));
      }
    });

    // Handle change events on the router.
    // If this is a nested router, propogate to the parent,
    // otherwise trigger a history change.
    router.on('change', (path, replace, useRoot) => {
      if (!this.context) {
        return this.history[replace ? 'replace' : 'push'](path);
      }

      let pathToTrigger = path;
      if (!useRoot && this.context.prefix !== '/') {
        pathToTrigger = this.context.prefix.replace(/(\/$)/, '') + path;
      }
      return this.context.router.trigger(pathToTrigger, replace);
    });

    return (
      <RouterContext.Provider value={context}>
        {router.dispatch(hasContext ? this.context.route : this.state.route)}
      </RouterContext.Provider>
    );
  }
}

Router.propTypes = {
  children: PropTypes.node
};

Router.contextType = RouterContext;

export default Router;
